import { Form, Formik } from "formik";
import {
  CreateCustomerDto,
  PatchCustomerDto,
  Customer,
} from "@/models/Customer.ts";
import * as yup from "yup";
import { FormikInput } from "@/components/common/form/FormikInput.tsx";
import { FormikSelect } from "@/components/common/form/FomikSelect.tsx";
import { Button } from "@/components/base/Button.tsx";
import { PhoneType } from "@/types/Phone.ts";
import { Autocomplete } from "@/components/base/Autocomplete.tsx";
import { useEffect, useState } from "react";
import { Option } from "@/types/Option.ts";
import { generator as generatorActions } from "@/repositories/generator.ts";
import { LoadingPage } from "@/components/page/LoadingPage.tsx";
import { Label } from "@/components/base/Label.tsx";

type CreateEditCustomerFormProps = {
  onSubmit: (values: CreateCustomerDto | PatchCustomerDto) => Promise<void>;
  initialCustomerData?: Customer;
  onCancel?: () => void;
  tenantId: string;
  isEditable?: boolean;
};

const validationSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
});

export const CreateEditCustomerForm = ({
  initialCustomerData,
  onCancel,
  onSubmit,
  tenantId,
  isEditable,
}: CreateEditCustomerFormProps) => {
  const [availableGenerators, setAvailableGenerators] =
    useState<{ option: Option; id: string }[]>();
  const [isLoading, setIsloading] = useState<boolean>(false);

  useEffect(() => {
    setIsloading(true);
    generatorActions
      .getOptions(tenantId)
      .then((result) =>
        setAvailableGenerators(
          result.index
            .filter(
              (item) =>
                item.customerId === "" ||
                (initialCustomerData
                  ? item.customerId === initialCustomerData.id
                  : false)
            )
            .map((item) => ({
              option: {
                value: `${item.make} ${item.modelNumber}: ${item.serialNumber}`,
                label: `${item.make} ${item.modelNumber}: ${item.serialNumber}`,
              },
              id: item.id,
            }))
        )
      )
      .finally(() => setIsloading(false));
  }, []);
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const findGeneratorId = (value: string) => {
    return availableGenerators?.find((item) => item.option.value === value)?.id;
  };

  const findSearchTerm = (id: string) => {
    const generator = availableGenerators?.find((item) => item.id === id);
    if (!generator) {
      return "";
    }
    return generator.option.value;
  };
  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <Formik
      initialValues={{
        generatorSearchTerm: initialCustomerData?.generatorId
          ? findSearchTerm(initialCustomerData?.generatorId)
          : "",
        firstName: initialCustomerData?.firstName ?? "",
        lastName: initialCustomerData?.lastName ?? "",
        email: initialCustomerData?.email ?? "",
        phoneNumber: initialCustomerData?.phone?.phoneNumber ?? "",
        phoneNumberType: initialCustomerData?.phone?.type ?? "",
        addressStreet: initialCustomerData?.address?.address1 ?? "",
        addressStreet2: initialCustomerData?.address?.address2 ?? "",
        addressCity: initialCustomerData?.address?.city ?? "",
        addressZip: initialCustomerData?.address?.zip ?? "",
        addressState: initialCustomerData?.address?.state ?? "",
        notes: initialCustomerData?.notes ?? "",
        meterNumber: initialCustomerData?.meterNumber ?? "",
        serviceAmp: initialCustomerData?.serviceAmp ?? "",
        generatorId: initialCustomerData?.generatorId ?? "",
      }}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        let submitData: CreateCustomerDto | PatchCustomerDto = {
          tenantId: tenantId,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: {
            phoneNumber: values.phoneNumber,
            type: values.phoneNumberType as PhoneType,
          },
          address: {
            address1: values.addressStreet,
            address2: values.addressStreet2,
            city: values.addressCity,
            state: values.addressState,
            zip: values.addressZip,
          },
          notes: values.notes,
          meterNumber: values.meterNumber,
          serviceAmp: values.serviceAmp,
          generatorId: values.generatorId,
        } as CreateCustomerDto;
        if (initialCustomerData?.id) {
          submitData = {
            id: initialCustomerData.id,
            ...submitData,
          } as PatchCustomerDto;
        }

        onSubmit(submitData).finally(() => setSubmitting(false));
        return;
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <Form
          className="flex flex-col sm:grid sm:grid-cols-3 gap-4 m-auto w-64 sm:w-auto max-w-screen-md"
          onSubmit={handleSubmit}
        >
          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Customer Information</h3>
            <FormikInput
              name="firstName"
              type="text"
              id="firstName"
              label="First Name"
              disabled={!isEditable}
            />
            <FormikInput
              name="lastName"
              type="text"
              id="lastName"
              label="Last Name"
              disabled={!isEditable}
            />
            <FormikInput
              name="email"
              type="text"
              id="email"
              label="Email"
              disabled={!isEditable}
            />
            <FormikInput
              name="phoneNumber"
              type="text"
              id="phoneNumber"
              label="Phone Number"
              disabled={!isEditable}
            />
            <FormikSelect
              name="phoneNumberType"
              id="phoneNumberType"
              label="Phone Type"
              options={[
                { value: "mobile", label: "Mobile" },
                { value: "work", label: "Work" },
                { value: "home", label: "Home" },
                { value: "other", label: "Other" },
              ]}
              disabled={!isEditable}
            />
          </div>

          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Service Address</h3>

            <FormikInput
              name="addressStreet"
              type="text"
              id="addressStreet"
              label="Street Address"
              disabled={!isEditable}
            />
            <FormikInput
              name="addressStreet2"
              type="text"
              id="addressStreet2"
              label="Street Address 2"
              disabled={!isEditable}
            />
            <FormikInput
              name="addressCity"
              type="text"
              id="addressCity"
              label="City"
              disabled={!isEditable}
            />
            <FormikInput
              name="addressState"
              type="text"
              id="addressState"
              label="State"
              disabled={!isEditable}
            />
            <FormikInput
              name="addressZip"
              type="text"
              id="addressZip"
              label="Zip"
              disabled={!isEditable}
            />
          </div>

          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Service Information</h3>

            <div className="flex flex-col gap-2">
              <Label htmlFor={"generatorSearchTerm"}>Generator</Label>
              <Autocomplete
                autocompleteOptions={
                  availableGenerators?.map((item) => item.option) ?? []
                }
                value={values.generatorSearchTerm}
                setValue={(value) => {
                  setFieldValue("generatorId", findGeneratorId(value));
                  setFieldValue("generatorSearchTerm", value);
                }}
                id="generatorSeachTerm"
                disabled={!isEditable}
              />
            </div>
            <FormikInput
              name="generatorId"
              type="text"
              id="generatorId"
              label="Generator ID"
              disabled={true}
            />
            <FormikInput
              name="meterNumber"
              type="text"
              id="meterNumber"
              label="Meter Number"
              disabled={!isEditable}
            />
            <FormikInput
              name="serviceAmp"
              type="text"
              id="serviceAmp"
              label="Service Amp"
              disabled={!isEditable}
            />
          </div>
          {isEditable && (
            <div className="flex md:col-span-3 flex-row gap-4 justify-end">
              <Button
                variant={"secondary"}
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
